import { createContext, useContext } from 'react';

/** The toast after a page save (spec "Save confirmation toast"). */
export const CHANGES_SAVED = 'Changes saved';

/** How long a toast stays up before it dismisses itself: the spec's "about 4 to 5 seconds". */
export const TOAST_DURATION_MS = 4500;

/**
 * The CSS custom property a sticky Save footer (StickySaveFooter) keeps at its own height, in px, on
 * <html> while it is on screen, so the toast sits above it instead of over it. Unset when there is no
 * footer. The toast's position class spells the name out (Tailwind builds only classes it can read in
 * the source), and ToastProvider.spec pins that the two agree.
 */
export const STICKY_FOOTER_HEIGHT_VAR = '--sticky-save-footer-h';

export interface ToastApi {
  /**
   * Shows `message` (default "Changes saved") for about 4.5 seconds, replacing any toast on screen.
   * Call it only once a save has SUCCEEDED: a failed save keeps its own error and shows no toast.
   */
  show: (message?: string) => void;
  /** Hides the toast on screen, if there is one. */
  dismiss: () => void;
}

const NO_TOAST: ToastApi = { show: () => undefined, dismiss: () => undefined };

/** Provided by ToastProvider (App.tsx). The default does nothing: see useToast. */
export const ToastContext = createContext<ToastApi>(NO_TOAST);

/**
 * The app's one toast: `const toast = useToast(); ... toast.show(CHANGES_SAVED)` after a save
 * succeeds. Outside a ToastProvider (a card rendered on its own in a spec) it does nothing rather than
 * throw, so a card that confirms its saves needs no provider in the specs that never look for one.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}
